// Email service for automated shipment and payment notifications

interface EmailPayload {
  emailType: 'payment_confirmation' | 'shipment_status_update' | 'delivery_milestone' | 'customs_clearance';
  recipientEmail: string;
  data: {
    trackingNumber: string;
    status?: string;
    location?: string;
    timestamp: string;
    estimatedDelivery?: string;
    description?: string;
    amount?: number;
    paymentMethod?: string;
    milestone?: string;
    nextStep?: string;
    countryCode?: string;
  };
}

const SUPABASE_URL = process.env.NEXT_PUBLIC_SUPABASE_URL;
const EDGE_FUNCTION_URL = `${SUPABASE_URL}/functions/v1/send-shipment-email`;

export async function sendShipmentEmail(payload: EmailPayload): Promise<{ success: boolean; message?: string; error?: string }> {
  try {
    if (!SUPABASE_URL) {
      console.error('Supabase URL not configured');
      return { success: false, error: 'Email service not configured' };
    }

    const response = await fetch(EDGE_FUNCTION_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    const result = await response.json();

    if (!response.ok) {
      console.error('Email sending failed:', result);
      return { success: false, error: result.error || 'Failed to send email' };
    }

    return { success: true, message: 'Email sent successfully' };
  } catch (error) {
    console.error('Email service error:', error);
    return { success: false, error: 'Network error while sending email' };
  }
}

// Helper functions for specific email types
export async function sendPaymentConfirmationEmail(
  recipientEmail: string,
  trackingNumber: string,
  amount: number,
  paymentMethod: string
) {
  return sendShipmentEmail({
    emailType: 'payment_confirmation',
    recipientEmail,
    data: {
      trackingNumber,
      amount,
      paymentMethod,
      timestamp: new Date().toISOString(),
    },
  });
}

export async function sendShipmentStatusEmail(
  recipientEmail: string,
  trackingNumber: string,
  status: string,
  location: string,
  estimatedDelivery?: string,
  description?: string
) {
  return sendShipmentEmail({
    emailType: 'shipment_status_update',
    recipientEmail,
    data: {
      trackingNumber,
      status,
      location,
      estimatedDelivery,
      description,
      timestamp: new Date().toISOString(),
    },
  });
}

export async function sendDeliveryMilestoneEmail(
  recipientEmail: string,
  trackingNumber: string,
  milestone: string,
  location: string,
  nextStep?: string
) {
  return sendShipmentEmail({
    emailType: 'delivery_milestone',
    recipientEmail,
    data: {
      trackingNumber,
      milestone,
      location,
      nextStep,
      timestamp: new Date().toISOString(),
    },
  });
}

export async function sendCustomsClearanceEmail(
  recipientEmail: string,
  trackingNumber: string,
  location: string,
  countryCode: string
) {
  return sendShipmentEmail({
    emailType: 'customs_clearance',
    recipientEmail,
    data: {
      trackingNumber,
      location,
      countryCode,
      timestamp: new Date().toISOString(),
    },
  });
}
